<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <!-- Top bar: search + stats + save -->
    <div class="card mb-3">
      <div class="card-body py-3">
        <div class="d-flex align-items-center justify-content-between flex-wrap" style="gap: 12px">
          <!-- Search -->
          <div class="search-box" style="min-width: 280px; max-width: 360px; flex: 1">
            <div class="position-relative">
              <input
                type="text"
                class="form-control"
                v-model="searchQuery"
                placeholder="Buscar campo por nome..."
              />
              <i class="bx bx-search-alt search-icon"></i>
            </div>
          </div>

          <!-- Save -->
          <div class="d-flex align-items-center" style="gap: 10px">
            <transition name="fade">
              <span v-if="hasChanges" class="badge badge-soft-warning font-size-12 px-3 py-2">
                <i class="bx bx-error-circle mr-1"></i>Não salvo
              </span>
            </transition>
            <button
              type="button"
              class="btn btn-success"
              :disabled="saving || !hasChanges"
              @click="saveAll"
            >
              <i class="bx mr-1" :class="saving ? 'bx-loader-alt bx-spin' : 'bx-save'"></i>
              {{ saving ? 'Salvando...' : 'Salvar' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="card">
      <div class="card-body text-center py-5">
        <div class="spinner-border text-primary mb-3" role="status"></div>
        <p class="text-muted mb-0 font-size-14">Carregando campos disponíveis...</p>
      </div>
    </div>

    <!-- Catalog -->
    <div v-else>
      <div
        v-for="section in filteredCatalog"
        :key="section.category"
        class="card section-card mb-3"
      >
        <!-- Section header -->
        <div
          class="card-header section-toggle d-flex align-items-center justify-content-between"
          @click="toggleSection(section.category)"
        >
          <div class="d-flex align-items-center">
            <div class="section-icon mr-3">
              <i class="bx font-size-20" :class="sectionIcon(section.category)"></i>
            </div>
            <div>
              <h6 class="mb-0 font-size-14 font-weight-semibold">{{ section.label }}</h6>
            </div>
          </div>
          <div class="d-flex align-items-center">
            <i
              class="bx font-size-20 text-muted transition-transform"
              :class="isSectionOpen(section.category) ? 'bx-chevron-up' : 'bx-chevron-down'"
            ></i>
          </div>
        </div>

        <!-- Section body -->
        <transition name="slide">
          <div v-show="isSectionOpen(section.category)">
            <div class="card-body p-0">
              <draggable
                :list="section.fields"
                :options="{ animation: 200, handle: '.drag-handle', group: 'fields' }"
                @change="onDragChange"
              >
                <div
                  v-for="(field, fIdx) in section.fields"
                  :key="field.key"
                  class="field-item d-flex align-items-center px-3 py-2"
                  :class="{
                    'field-item--active': field.enabled,
                    'border-top': fIdx > 0
                  }"
                >
                  <!-- Drag handle -->
                  <div
                    class="drag-handle text-muted mr-3"
                    :class="{ 'visible': field.enabled, 'invisible': !field.enabled }"
                  >
                    <i class="bx bx-grid-vertical font-size-18"></i>
                  </div>

                  <!-- Toggle -->
                  <div class="mr-3">
                    <b-form-checkbox
                      v-model="field.enabled"
                      switch
                      size="lg"
                      @change="markChanged"
                    ></b-form-checkbox>
                  </div>

                  <!-- Info -->
                  <div class="flex-grow-1" style="min-width: 0">
                    <span class="font-size-14 d-block text-truncate" :class="field.enabled ? 'text-dark font-weight-medium' : 'text-muted'">
                      {{ field.label }}
                    </span>
                  </div>

                  <!-- Component type badge -->
                  <span
                    class="badge font-size-11 px-2 py-1 ml-2"
                    :class="componentBadge(field.component)"
                  >
                    {{ componentLabel(field.component) }}
                  </span>
                </div>
              </draggable>

              <!-- Empty section after search -->
              <div v-if="section.fields.length === 0" class="text-center text-muted py-4 font-size-13">
                Nenhum campo nesta seção
              </div>
            </div>
          </div>
        </transition>
      </div>

      <!-- No results -->
      <div v-if="filteredCatalog.length === 0 && searchQuery" class="card">
        <div class="card-body text-center py-5">
          <i class="bx bx-search-alt font-size-24 text-muted d-block mb-2"></i>
          <p class="text-muted mb-0">Nenhum campo encontrado para "<strong>{{ searchQuery }}</strong>"</p>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import draggable from 'vuedraggable'
import Layout from '@/components/layout/vertical'
import PageHeader from '@/components/page-header'
import appConfig from '@/app.config'
import { errorMessage, successMessage } from '@/components/alertsMessages'
import { getAvailableFields, syncFilters } from './services'

export default {
  components: {
    draggable,
    Layout,
    PageHeader
  },
  name: 'Configuração de Filtros',
  page: {
    title: 'Configuração de Filtros',
    meta: [{ name: 'description', content: appConfig.description }]
  },
  data () {
    return {
      loading: true,
      saving: false,
      searchQuery: '',
      hasChanges: false,
      catalog: [],
      openSections: {},
      title: 'Configuração de Filtros',
      items: [
        { text: 'Configurações' },
        { text: 'Filtros', active: true }
      ]
    }
  },
  computed: {
    filteredCatalog () {
      if (!this.searchQuery.trim()) return this.catalog
      const q = this.searchQuery.toLowerCase().trim()
      return this.catalog
        .map(section => ({
          ...section,
          fields: section.fields.filter(f =>
            f.label.toLowerCase().includes(q) || f.key.toLowerCase().includes(q)
          )
        }))
        .filter(section => section.fields.length > 0)
    }
  },
  mounted () {
    this.loadCatalog()
  },
  methods: {
    async loadCatalog () {
      this.loading = true
      try {
        const res = await getAvailableFields('participants')
        this.catalog = res.data.data || []
        this.catalog.forEach(section => {
          this.$set(this.openSections, section.category, section.category === 'standard')
        })
        this.hasChanges = false
      } catch (err) {
        console.error(err)
        errorMessage('Falha ao carregar campos disponíveis.')
      } finally {
        this.loading = false
      }
    },

    toggleSection (category) {
      this.$set(this.openSections, category, !this.openSections[category])
    },
    isSectionOpen (category) {
      return !!this.openSections[category]
    },
    markChanged () {
      this.hasChanges = true
    },
    onDragChange () {
      this.hasChanges = true
    },

    sectionIcon (category) {
      if (category === 'standard') return 'bx-user'
      if (category === 'card_fields') return 'bx-id-card'
      if (category.startsWith('form_')) return 'bx-list-ul'
      if (category.startsWith('rc_')) return 'bx-check-shield'
      return 'bx-category'
    },

    componentBadge (component) {
      const map = {
        'text': 'badge-soft-primary',
        'select': 'badge-soft-info',
        'multiselect': 'badge-soft-warning',
        'date-range': 'badge-soft-secondary'
      }
      return map[component] || 'badge-soft-secondary'
    },
    componentLabel (component) {
      const map = {
        'text': 'Texto',
        'select': 'Seleção',
        'multiselect': 'Múltipla',
        'date-range': 'Período'
      }
      return map[component] || component
    },

    async saveAll () {
      this.saving = true
      try {
        const fields = []
        let order = 1
        this.catalog.forEach(section => {
          section.fields.forEach(field => {
            fields.push({
              key: field.key,
              enabled: field.enabled,
              order: field.enabled ? order++ : 999
            })
          })
        })
        await syncFilters('participants', fields)
        successMessage('Filtros salvos com sucesso!', 2500)
        this.hasChanges = false
      } catch (err) {
        console.error(err)
        errorMessage('Falha ao salvar filtros.')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
/* Section card */
.section-card {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e9ecef;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

/* Section toggle header */
.section-toggle {
  cursor: pointer;
  user-select: none;
  background: #fff;
  border-bottom: 1px solid #f0f0f0;
  padding: 14px 16px;
  transition: background-color 0.15s;
}
.section-toggle:hover {
  background-color: #fafbfc;
}

/* Section icon circle */
.section-icon {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  background: #f1f3f7;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #556ee6;
}

/* Field rows */
.field-item {
  transition: background-color 0.15s;
  min-height: 52px;
  border-color: #f0f0f0 !important;
}
.field-item:hover {
  background-color: #fafbfc;
}
.field-item--active {
  background-color: #f6fef9;
}
.field-item--active:hover {
  background-color: #ecfdf3;
}

/* Drag handle */
.drag-handle {
  cursor: move;
  opacity: 0.35;
  transition: opacity 0.15s;
}
.field-item:hover .drag-handle.visible {
  opacity: 0.8;
}
.invisible {
  visibility: hidden;
}

/* Transitions */
.transition-transform {
  transition: transform 0.2s;
}
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
.slide-enter-active, .slide-leave-active {
  transition: all 0.25s ease;
  overflow: hidden;
}
.slide-enter, .slide-leave-to {
  opacity: 0;
}

/* Fix Bootstrap Vue switch alignment */
.custom-switch {
  padding-left: 2.75rem;
}
</style>
